/**
 * Utility functions for handling multilingual text
 */

/**
 * Get text in the specified language from a multilingual object
 * @param {Object|string} textObj - Text object with language keys {ar: "...", en: "..."} or a plain string
 * @param {string} lang - Language code ('en', 'pt' or 'ar')
 * @param {string} fallback - Fallback text if nothing is found
 * @returns {string} The text in the specified language
 */
export const getText = (textObj, lang = 'en', fallback = '') => {
  // If it's already a string, return it
  if (typeof textObj === 'string') {
    return textObj;
  }
  
  // If it's null or undefined, return fallback
  if (!textObj) {
    return fallback;
  }
  
  // If it's an object with language keys, get the specified language
  if (typeof textObj === 'object') {
    // Try to get the specified language
    if (textObj[lang]) {
      return textObj[lang];
    }
    
    // Fallback to English, then Portuguese, then Arabic.
    if (textObj.en) {
      return textObj.en;
    }

    if (textObj.pt) {
      return textObj.pt;
    }
    
    // Fallback to Arabic
    if (textObj.ar) {
      return textObj.ar;
    }
    
    // If no language keys found, return fallback
    return fallback;
  }
  
  return fallback;
};

/**
 * Check if a text object is multilingual
 * @param {*} textObj - The object to check
 * @returns {boolean} True if the object has language keys
 */
export const isMultilingual = (textObj) => {
  return (
    textObj &&
    typeof textObj === 'object' &&
    (textObj.hasOwnProperty('en') || textObj.hasOwnProperty('pt') || textObj.hasOwnProperty('ar'))
  );
};

/**
 * Format multilingual text for display
 * Shows both languages if available, with the primary language first
 * @param {Object|string} textObj - Text object with language keys or plain string
 * @param {string} primaryLang - Primary language to display first
 * @returns {string} Formatted text
 */
export const formatMultilingualText = (textObj, primaryLang = 'en') => {
  if (typeof textObj === 'string') {
    return textObj;
  }
  
  if (!textObj || typeof textObj !== 'object') {
    return '';
  }
  
  const primary = textObj[primaryLang];
  const secondary = primaryLang === 'ar' ? (textObj.en || textObj.pt) : textObj.ar;
  
  if (primary && secondary) {
    return `${primary} / ${secondary}`;
  }
  
  return primary || secondary || '';
};
